import axios from "axios";
import type { REST_schema } from "../@types/sharedSchema";
import { firebaseApp } from "../firebaseConfig";
import { WEBOT_SERVER_ENDPOINT } from "../logic/Consts";
import type { SignaturePageSlice } from "./signaturePagination";

export type SignerFieldType =
  | "heading" | "text" | "signature" | "initials" | "date" | "number"
  | "image" | "checkbox" | "multiple" | "file" | "radio" | "select"
  | "cells" | "stamp" | "payment" | "phone" | "verification";

export type ExtractedField = Omit<REST_schema.SignatureSendField, "type"> & {
  type: SignerFieldType;
};

export type HtmlToPdfResult = Omit<REST_schema.RenderSignatureDocumentResponse, "fields"> & {
  fields: ExtractedField[];
  /** Compiler metadata; optional when talking to an older renderer. */
  pageSlices?: SignaturePageSlice[];
};

// Bounded, memory-only, per-user cache. Preview and send reuse the exact
// compilation when their resolved HTML matches; nothing enters localStorage.
const compilations = new Map<string, { expiresAt: number; result: Promise<HtmlToPdfResult> }>();
const CACHE_TTL_MS = 10 * 60 * 1000;
const CACHE_SIZE = 6;

/** The server owns layout, pagination and field measurement. Browsers only
 * display its PDF; there is deliberately no local-render fallback. */
export async function renderHtmlToPdfWithFields(html: string): Promise<HtmlToPdfResult> {
  const user = firebaseApp.auth().currentUser;
  if (!user) throw new Error("Authentication is required to prepare a document");
  const key = `${user.uid}\n${html}`;
  const now = Date.now();
  compilations.forEach((entry, entryKey) => {
    if (entry.expiresAt <= now) compilations.delete(entryKey);
  });
  const cached = compilations.get(key);
  if (cached) return cached.result;
  const result = (async () => {
    const token = await user.getIdToken();
    for (let attempt = 0; ; attempt += 1) {
      try {
        const response = await axios.post<HtmlToPdfResult>(
          `${WEBOT_SERVER_ENDPOINT}/api/v1/signatures/render`,
          { html },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
        );
        const artifact = response.data;
        if (!artifact.pdfBase64 || !artifact.pdfSha256 || !artifact.renderVersion ||
            !Number.isInteger(artifact.totalPages) || artifact.totalPages < 1 ||
            artifact.pageDimensions?.length !== artifact.totalPages || !Array.isArray(artifact.fields)) {
          throw new Error("The prepared document is incomplete");
        }
        return artifact;
      } catch (error) {
        if (attempt < 2 && axios.isAxiosError(error) && error.response?.status === 429) {
          await new Promise((resolve) => window.setTimeout(resolve, (attempt + 1) * 2000));
          continue;
        }
        throw error;
      }
    }
  })();
  compilations.set(key, { expiresAt: now + CACHE_TTL_MS, result });
  while (compilations.size > CACHE_SIZE) compilations.delete(compilations.keys().next().value!);
  result.catch(() => {
    if (compilations.get(key)?.result === result) compilations.delete(key);
  });
  return result;
}
